import { seededRandom, shade } from '../pixel.js';
import type { PixelCanvas, Point } from '../pixel.js';
import {
  HH,
  HW,
  box,
  drawBox,
  drawBoxes,
  drawCylinder,
  faceQuad,
  faces,
  frontFace,
  iso,
  seatWithBack,
  turn,
} from './iso.js';
import type { BoxPiece, Face, FurniDrawer } from './iso.js';

/** Drawers for the home, wall and garden furniture (see protocol furniture/home.ts). */

type Drawer = FurniDrawer;

const WOOD = '#b98a5c';
const WOOD_DARK = '#8a5a34';
const WHITE = '#f1eee6';
const STEEL = '#b9c0c6';
const LEAF = '#3d9e4c';
const LEAF_DARK = '#2f7f3c';
const LEAF_LIGHT = '#5cbd63';

const flatDiamond = (pc: PixelCanvas, r: number, color: string, h = 0) =>
  pc.poly([iso(-r, -r, h), iso(r, -r, h), iso(r, r, h), iso(-r, r, h)], color);

/** The face a rotation looks at; items turned away from the viewer show their left face. */
const facing = (rotation: number): Face => frontFace(rotation) ?? 'left';

/** A point on a visible face, `s` along it and `h` up. */
const onFace = (face: Face, at: number, s: number, h: number): Point =>
  face === 'left' ? iso(s, at, h) : iso(at, s, h);

/** Whether a wall-like item runs along the tile's u axis (rotations 0 and 4) or its v axis (2 and 6). */
const alongU = (rotation: number) => rotation === 0 || rotation === 4;

/** A thin slab running along the tile edge, for walls and fences. */
function slab(
  rotation: number,
  thickness: number,
  h0: number,
  h1: number,
  colors: ReturnType<typeof faces>,
  span = 0.5,
): BoxPiece {
  return alongU(rotation)
    ? box(-span, span, -thickness, thickness, h0, h1, colors)
    : box(-thickness, thickness, -span, span, h0, h1, colors);
}

/** Paints a quad on the visible long side of a slab. */
function slabFace(
  pc: PixelCanvas,
  rotation: number,
  thickness: number,
  s0: number,
  s1: number,
  h0: number,
  h1: number,
  color: string,
): void {
  if (alongU(rotation)) faceQuad(pc, 'left', thickness, s0, s1, h0, h1, color);
  else faceQuad(pc, 'right', thickness, s0, s1, h0, h1, color);
}

/* -------------------------------------------------------------------------- */
/* Living room                                                                */
/* -------------------------------------------------------------------------- */

interface SofaTheme {
  base: string;
  seat: readonly [string, string];
  back: readonly [string, string];
}

const SOFA_THEMES: Record<string, SofaTheme> = {
  blue: { base: '#2e3f5c', seat: ['#4a6fa5', '#6a8fc5'], back: ['#3d5d8c', '#5878ad'] },
  grey: { base: '#3a3a40', seat: ['#7a7a82', '#9a9aa2'], back: ['#63636b', '#82828a'] },
  red: { base: '#4a2020', seat: ['#a5484a', '#c56a6c'], back: ['#8c3d3d', '#ad5858'] },
};

/** An L-shaped corner to connect `sofaDrawer` pieces into a sectional; rotate to pick which corner. */
function cornerSofaDrawer(theme: SofaTheme): Drawer {
  return (pc, rotation) => {
    const { su, sv } = cornerQuadrant(rotation);
    const seat = faces(...theme.seat);
    const back = faces(...theme.back);
    const backU = su > 0 ? box(0.3, 0.46, -0.46, 0.46, 16, 38, back) : box(-0.46, -0.3, -0.46, 0.46, 16, 38, back);
    const backV = sv > 0 ? box(-0.46, 0.46, 0.3, 0.46, 16, 38, back) : box(-0.46, 0.46, -0.46, -0.3, 16, 38, back);
    drawBox(pc, box(-0.46, 0.46, -0.46, 0.46, 0, 6, faces(theme.base)));
    drawBoxes(pc, [box(-0.46, 0.46, -0.46, 0.46, 6, 16, seat), backU, backV]);
    // Cushion seam pointing into the open corner.
    const seam = iso(-su * 0.15, -sv * 0.15, 16);
    pc.rect(seam.x - 1, seam.y - 1, 2, 1, theme.base);
  };
}

function sofaDrawer(theme: SofaTheme): Drawer {
  return (pc, rotation) => {
    drawBox(pc, box(-0.46, 0.46, -0.46, 0.46, 0, 6, faces(theme.base)));
    drawBoxes(pc, seatWithBack(rotation, 0.46, [6, 16], 38, 0.16, faces(...theme.seat), faces(...theme.back)));
    // Cushion seam.
    const s = iso(0, 0, 16);
    pc.rect(s.x - 1, s.y - 1, 2, 1, theme.base);
  };
}

const drawFloorCushion: Drawer = (pc) => {
  drawCylinder(pc, 0.32, 0, 7, '#f08a6a', '#d9673f');
  const t = iso(0, 0, 7);
  pc.px(t.x, t.y, '#ffd0b8');
  pc.px(t.x - 1, t.y, '#c85a34');
};

const drawWoodenBench: Drawer = (pc, rotation) => {
  for (const [u, v] of [
    [-0.4, -0.4],
    [0.4, -0.4],
    [0.4, 0.4],
    [-0.4, 0.4],
  ] as const) {
    drawBox(pc, box(u - 0.05, u + 0.05, v - 0.05, v + 0.05, 0, 12, faces(WOOD_DARK)));
  }
  drawBoxes(
    pc,
    seatWithBack(
      rotation,
      0.46,
      [12, 16],
      32,
      0.05,
      faces(WOOD, '#d3a574'),
      faces(WOOD_DARK, WOOD),
    ),
  );
};

const drawDiningChair: Drawer = (pc, rotation) => {
  for (const [u, v] of [
    [-0.22, -0.22],
    [0.22, -0.22],
    [0.22, 0.22],
    [-0.22, 0.22],
  ] as const) {
    drawBox(pc, box(u - 0.04, u + 0.04, v - 0.04, v + 0.04, 0, 14, faces(WOOD_DARK)));
  }
  drawBoxes(
    pc,
    seatWithBack(
      rotation,
      0.27,
      [14, 17],
      38,
      0.05,
      faces('#e0b07a', '#f0c790'),
      faces('#c99562', '#dcae7c'),
    ),
  );
};

const drawDiningTable: Drawer = (pc) => {
  for (const [u, v] of [
    [-0.38, -0.38],
    [0.38, -0.38],
    [0.38, 0.38],
    [-0.38, 0.38],
  ] as const) {
    drawBox(pc, box(u - 0.06, u + 0.06, v - 0.06, v + 0.06, 0, 24, faces(WOOD_DARK)));
  }
  drawBox(pc, box(-0.5, 0.5, -0.5, 0.5, 24, 28, faces(WOOD, '#dcae7c')));
  // A bowl of fruit.
  const bowl = iso(0, 0, 28);
  pc.ellipse(bowl.x, bowl.y - 1, 6, 2.5, '#efe8d8');
  pc.rect(bowl.x - 3, bowl.y - 4, 3, 3, '#e8503a');
  pc.rect(bowl.x, bowl.y - 4, 3, 3, '#f0bf36');
};

const drawSideTable: Drawer = (pc) => {
  drawCylinder(pc, 0.14, 0, 2, '#5a4030', '#40301f');
  drawCylinder(pc, 0.04, 2, 18, '#5a4030', '#40301f');
  drawCylinder(pc, 0.32, 18, 21, '#dcae7c', '#8a5a34');
};

const drawTvStand: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  const wide = alongU(rotation);
  drawBox(pc, wide ? box(-0.46, 0.46, -0.3, 0.3, 0, 14, faces('#5a4030', '#7a5a40')) : box(-0.3, 0.3, -0.46, 0.46, 0, 14, faces('#5a4030', '#7a5a40')));
  drawBox(pc, wide ? box(-0.4, 0.4, -0.06, 0.06, 15, 42, faces('#22242c', '#34363e')) : box(-0.06, 0.06, -0.4, 0.4, 15, 42, faces('#22242c', '#34363e')));
  const at = 0.06;
  faceQuad(pc, face, at, -0.36, 0.36, 18, 39, '#0f1420');
  // A glowing show.
  faceQuad(pc, face, at, -0.3, 0.05, 22, 34, '#3a7fbf');
  faceQuad(pc, face, at, -0.1, 0.3, 26, 37, '#e8a23a');
};

const drawPiano: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.46, 0.46, -0.28, 0.28, 0, 46, faces('#2a2530', '#3d3745')));
  drawBox(pc, box(-0.46, 0.46, -0.36, 0.36, 26, 30, faces('#1e1a24', '#2f2a36')));
  // Keys along the front face.
  const at = 0.36;
  faceQuad(pc, face, at, -0.44, 0.44, 26, 30, WHITE);
  for (let i = 0; i < 8; i += 1)
    faceQuad(pc, face, at + 0.005, -0.4 + i * 0.11, -0.4 + i * 0.11 + 0.02, 26, 30, '#3a3a44');
  const music = iso(0, 0, 46);
  pc.rect(music.x - 4, music.y - 5, 8, 4, '#f4f0e4');
  pc.px(music.x - 2, music.y - 3, '#333');
  pc.px(music.x + 1, music.y - 4, '#333');
};

const drawFireplace: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.48, 0.48, -0.3, 0.3, 0, 40, faces('#b5573f', '#cf7358')));
  drawBox(pc, box(-0.5, 0.5, -0.34, 0.34, 40, 46, faces('#7a5a40', '#9a7a58')));
  faceQuad(pc, face, 0.3, -0.26, 0.26, 2, 26, '#1e1418');
  faceQuad(pc, face, 0.3, -0.2, 0.2, 2, 12, '#e8843a');
  faceQuad(pc, face, 0.3, -0.1, 0.1, 2, 18, '#ffd84a');
  const logs = onFace(face, 0.3, 0, 4);
  pc.rect(logs.x - 6, logs.y - 1, 12, 2, '#5a3a20');
};

const drawGrandfatherClock: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.26, 0.26, -0.22, 0.22, 0, 12, faces('#6a4028', '#8a5a34')));
  drawBox(pc, box(-0.2, 0.2, -0.18, 0.18, 12, 58, faces('#7a4a2c', '#9a6a44')));
  drawBox(pc, box(-0.24, 0.24, -0.22, 0.22, 58, 70, faces('#6a4028', '#8a5a34')));
  const at = 0.22;
  const dial = onFace(face, at, 0, 64);
  pc.ellipse(dial.x, dial.y, 4, 4, '#f4f0e4');
  pc.line(dial.x, dial.y, dial.x, dial.y - 3, '#333');
  pc.line(dial.x, dial.y, dial.x + 2, dial.y, '#333');
  faceQuad(pc, face, at, -0.1, 0.1, 16, 50, '#2a1a12');
  const pend = onFace(face, at + 0.01, 0, 28);
  pc.ellipse(pend.x, pend.y, 3, 3, '#f0bf36');
  pc.rect(pend.x, pend.y - 14, 1, 14, '#f0bf36');
};

const drawAquarium: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  const wide = alongU(rotation);
  drawBox(pc, wide ? box(-0.46, 0.46, -0.3, 0.3, 0, 12, faces('#3a3a44', '#55555f')) : box(-0.3, 0.3, -0.46, 0.46, 0, 12, faces('#3a3a44', '#55555f')));
  drawBox(pc, wide ? box(-0.44, 0.44, -0.28, 0.28, 12, 40, faces('#7fc6e8', '#bfe8ff')) : box(-0.28, 0.28, -0.44, 0.44, 12, 40, faces('#7fc6e8', '#bfe8ff')));
  const at = 0.28;
  faceQuad(pc, face, at, -0.4, 0.4, 14, 38, '#5bb0d8');
  faceQuad(pc, face, at, -0.4, 0.4, 14, 17, '#e8d8a0');
  const rand = seededRandom(11);
  for (let i = 0; i < 4; i += 1) {
    const p = onFace(face, at, -0.3 + rand() * 0.6, 22 + rand() * 12);
    pc.rect(p.x, p.y, 3, 2, i % 2 ? '#ff8a3a' : '#f0bf36');
    pc.px(p.x + 3, p.y, i % 2 ? '#ff8a3a' : '#f0bf36');
  }
  const weed = onFace(face, at, 0.28, 16);
  pc.rect(weed.x, weed.y - 8, 1, 8, LEAF);
  pc.rect(weed.x + 1, weed.y - 6, 1, 6, LEAF_LIGHT);
};

const drawEasel: Drawer = (pc, rotation) => {
  const sideOn = rotation === 2 || rotation === 6;
  const back = rotation === 4;
  pc.line(-10, 0, -3, -46, WOOD_DARK);
  pc.line(10, 0, 3, -46, WOOD_DARK);
  pc.line(0, -2, 0, -40, WOOD_DARK);
  pc.rect(-11, -34, 22, 2, WOOD);
  if (sideOn) {
    // Viewed from the side, the canvas is a thin edge between the legs.
    pc.rect(-2, -58, 4, 24, '#f4f0e4');
    return;
  }
  pc.rect(-9, -58, 18, 24, back ? '#c8a874' : '#f4f0e4');
  if (back) return;
  pc.rect(-7, -54, 8, 8, '#4a8fd0');
  pc.rect(-1, -50, 7, 9, '#e8503a');
  pc.rect(-6, -44, 12, 3, '#3d9e4c');
};

const drawFloorVase: Drawer = (pc) => {
  pc.ellipse(0, -4, 8, 4, '#3a6fd6');
  pc.rect(-8, -20, 16, 16, '#efeae0');
  pc.ellipse(0, -22, 8, 5, '#efeae0');
  pc.rect(-8, -20, 3, 16, '#3a6fd6');
  pc.rect(5, -20, 3, 16, '#c8d4e8');
  pc.rect(-4, -30, 8, 8, '#efeae0');
  pc.rect(-5, -32, 10, 2, '#3a6fd6');
  pc.line(0, -32, -6, -46, LEAF_DARK);
  pc.line(0, -32, 5, -44, LEAF_DARK);
  pc.ellipse(-6, -47, 3, 3, '#ff7aa8');
  pc.ellipse(5, -45, 3, 3, '#ffd24a');
};

const drawStandingMirror: Drawer = (pc, rotation) => {
  const sideOn = rotation === 2 || rotation === 6;
  const back = rotation === 4;
  pc.line(-10, -4, -12, 0, '#6a4028');
  pc.line(10, -4, 12, 0, '#6a4028');
  if (sideOn) {
    // Viewed from the side, the mirror is a thin frame edge.
    pc.rect(-2, -54, 4, 50, '#8a5a34');
    return;
  }
  pc.rect(-9, -54, 18, 50, '#8a5a34');
  if (back) return;
  pc.rect(-7, -52, 14, 46, '#c8e4ee');
  pc.rect(-7, -52, 4, 46, '#e8f6fb');
  pc.line(-6, -30, -2, -46, '#ffffff');
};

function rugDrawer(outer: string, mid: string, inner: string, round: boolean): Drawer {
  return (pc) => {
    if (round) {
      pc.ellipse(0, 0, HW * 0.92, HH * 0.92, outer);
      pc.ellipse(0, 0, HW * 0.74, HH * 0.74, mid);
      pc.ellipse(0, 0, HW * 0.48, HH * 0.48, inner);
      return;
    }
    flatDiamond(pc, 0.47, outer);
    flatDiamond(pc, 0.4, mid);
    for (let i = -2; i <= 2; i += 1) {
      const a = iso(-0.36, i * 0.15);
      const b = iso(0.36, i * 0.15);
      pc.line(a.x, a.y, b.x, b.y, inner);
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Bedroom                                                                    */
/* -------------------------------------------------------------------------- */

const BED_LINENS: Record<string, readonly [string, string]> = {
  blue: ['#5b9bd5', '#7bb5e8'],
  white: ['#e8e4d8', '#f4f0e4'],
  grey: ['#8a8f96', '#a8adb4'],
};

const pillowSpot = (rotation: number, h: number) =>
  rotation === 2 ? iso(-0.3, 0, h) : rotation === 6 ? iso(0.3, 0, h) : rotation === 4 ? iso(0, -0.3, h) : iso(0, 0.3, h);

function bedDrawer(linen: readonly [string, string]): Drawer {
  return (pc, rotation) => {
    drawBox(pc, box(-0.46, 0.46, -0.46, 0.46, 0, 8, faces(WOOD_DARK)));
    drawBoxes(pc, seatWithBack(rotation, 0.46, [8, 14], 30, 0.1, faces(...linen), faces(WOOD, '#d3a574')));
    const p = pillowSpot(rotation, 14);
    pc.rect(p.x - 5, p.y - 3, 10, 3, WHITE);
    pc.rect(p.x - 5, p.y - 1, 10, 1, '#d8d4c8');
  };
}

/** A mattress laid straight on the floor: no frame, no headboard. */
function framelessBedDrawer(linen: readonly [string, string]): Drawer {
  return (pc, rotation) => {
    drawBox(pc, box(-0.46, 0.46, -0.46, 0.46, 0, 7, faces(...linen)));
    const p = pillowSpot(rotation, 7);
    pc.rect(p.x - 5, p.y - 3, 10, 3, WHITE);
    pc.rect(p.x - 5, p.y - 1, 10, 1, '#d8d4c8');
  };
}

const drawBunkBed: Drawer = (pc, rotation) => {
  for (const [u, v] of [
    [-0.44, -0.44],
    [0.44, -0.44],
    [0.44, 0.44],
    [-0.44, 0.44],
  ] as const) {
    drawBox(pc, box(u - 0.04, u + 0.04, v - 0.04, v + 0.04, 0, 56, faces(WOOD_DARK)));
  }
  drawBox(pc, box(-0.44, 0.44, -0.44, 0.44, 8, 14, faces('#e8703a', '#f08a58')));
  drawBox(pc, box(-0.44, 0.44, -0.44, 0.44, 34, 40, faces('#5bb078', '#78c894')));
  // The ladder is bolted to one corner post, which turns with the bed.
  const [lu, lv] = turn(0.46, 0.2, rotation);
  const l = iso(lu, lv, 0);
  pc.line(l.x, l.y, l.x, l.y - 36, WOOD);
  for (let h = 6; h < 34; h += 8) pc.rect(l.x - 2, l.y - h, 5, 1, WOOD);
};

function cabinetDrawer(
  body: string,
  top: string,
  height: number,
  doors: number,
  handle = '#f0bf36',
): Drawer {
  return (pc, rotation) => {
    const face = facing(rotation);
    drawBox(pc, box(-0.44, 0.44, -0.36, 0.36, 0, height, faces(body, top)));
    const at = 0.36;
    faceQuad(pc, face, at, -0.4, 0.4, 2, height - 2, shade(body, 0.92));
    const w = 0.8 / doors;
    for (let i = 0; i < doors; i += 1) {
      const s0 = -0.4 + i * w;
      faceQuad(pc, face, at, s0 + 0.01, s0 + w - 0.01, 3, height - 3, shade(body, 1.08));
      const hd = onFace(face, at, s0 + w * 0.7, height / 2);
      pc.rect(hd.x, hd.y, 1, 3, handle);
    }
  };
}

function drawerChest(body: string, top: string, height: number, drawers: number): Drawer {
  return (pc, rotation) => {
    const face = facing(rotation);
    drawBox(pc, box(-0.42, 0.42, -0.34, 0.34, 0, height, faces(body, top)));
    const at = 0.34;
    const step = (height - 4) / drawers;
    for (let i = 0; i < drawers; i += 1) {
      faceQuad(pc, face, at, -0.38, 0.38, 2 + i * step, 2 + (i + 1) * step - 1, shade(body, 1.08));
      const hd = onFace(face, at, 0, 2 + i * step + step / 2);
      pc.rect(hd.x - 2, hd.y - 1, 4, 1, '#f0bf36');
    }
  };
}

const drawNightstand: Drawer = (pc, rotation) => {
  drawerChest(WOOD, '#dcae7c', 24, 2)(pc, rotation);
  const lamp = iso(0, 0, 24);
  pc.rect(lamp.x - 1, lamp.y - 7, 2, 7, '#8a8a92');
  pc.poly(
    [
      { x: lamp.x - 5, y: lamp.y - 7 },
      { x: lamp.x + 5, y: lamp.y - 7 },
      { x: lamp.x + 3, y: lamp.y - 13 },
      { x: lamp.x - 3, y: lamp.y - 13 },
    ],
    '#f3e2b3',
  );
};

const drawTeddy: Drawer = (pc, rotation) => {
  // A small lean toward whichever side faces the viewer, so it isn't a flat mirror image.
  const lean = frontFace(rotation) === 'right' ? 2 : -2;
  pc.ellipse(lean, -5, 6, 6, '#b5793e');
  pc.ellipse(lean, -13, 5, 5, '#c98a4a');
  pc.ellipse(lean - 4, -17, 2, 2, '#b5793e');
  pc.ellipse(lean + 4, -17, 2, 2, '#b5793e');
  pc.ellipse(lean, -12, 2, 1.5, '#e8c89a');
  pc.px(lean - 2, -14, '#2a1e19');
  pc.px(lean + 2, -14, '#2a1e19');
  pc.ellipse(lean - 6, -7, 2, 3, '#b5793e');
  pc.ellipse(lean + 6, -7, 2, 3, '#b5793e');
  pc.rect(lean - 2, -9, 4, 1, '#d9433b');
};

/* -------------------------------------------------------------------------- */
/* Kitchen and bath                                                           */
/* -------------------------------------------------------------------------- */

const drawFridge: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.4, 0.4, -0.4, 0.4, 0, 60, faces('#dfe4e8', '#f4f7f9')));
  const at = 0.4;
  faceQuad(pc, face, at, -0.36, 0.36, 34, 35, '#9aa2a8');
  const h1 = onFace(face, at, 0.28, 44);
  const h2 = onFace(face, at, 0.28, 22);
  pc.rect(h1.x, h1.y, 1, 8, '#7a828a');
  pc.rect(h2.x, h2.y, 1, 8, '#7a828a');
  const magnet = onFace(face, at, -0.2, 48);
  pc.rect(magnet.x, magnet.y, 3, 3, '#e8503a');
  pc.rect(magnet.x + 4, magnet.y + 2, 3, 3, '#f0bf36');
};

const drawStove: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.44, 0.44, -0.4, 0.4, 0, 30, faces('#c9ced3', '#e0e4e8')));
  const at = 0.4;
  faceQuad(pc, face, at, -0.34, 0.34, 4, 18, '#2a2e34');
  faceQuad(pc, face, at, -0.3, 0.3, 6, 16, '#3d434a');
  for (const s of [-0.26, 0.26]) {
    const b = onFace(face, at, s, 24);
    pc.rect(b.x, b.y, 2, 2, '#e8503a');
  }
  const top = iso(0, 0, 30);
  pc.ellipse(top.x - 6, top.y - 1, 6, 2.5, '#2a2e34');
  pc.ellipse(top.x + 7, top.y + 1, 6, 2.5, '#2a2e34');
  pc.ellipse(top.x - 6, top.y - 4, 7, 3, '#4a4e56');
};

const drawKitchenCounter: Drawer = (pc, rotation) => {
  cabinetDrawer('#5a8fb0', '#e8e4da', 30, 2, STEEL)(pc, rotation);
};

const drawKitchenSink: Drawer = (pc, rotation) => {
  cabinetDrawer('#5a8fb0', '#e8e4da', 30, 2, STEEL)(pc, rotation);
  const top = iso(0, 0, 30);
  pc.ellipse(top.x, top.y, 9, 4, '#8a9298');
  pc.ellipse(top.x, top.y, 7, 3, '#5a636a');
  pc.rect(top.x - 1, top.y - 14, 2, 12, STEEL);
  pc.rect(top.x - 1, top.y - 14, 6, 2, STEEL);
};

const drawPantry: Drawer = (pc, rotation) => {
  cabinetDrawer('#d8c9a8', '#efe6cc', 60, 2, WOOD_DARK)(pc, rotation);
};

const drawWaterDispenser: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.26, 0.26, -0.26, 0.26, 0, 36, faces('#dfe4e8', '#f4f7f9')));
  faceQuad(pc, face, 0.26, -0.18, 0.18, 12, 18, '#5a636a');
  const tap1 = onFace(face, 0.26, -0.06, 24);
  const tap2 = onFace(face, 0.26, 0.06, 24);
  pc.rect(tap1.x, tap1.y, 3, 3, '#e8503a');
  pc.rect(tap2.x, tap2.y, 3, 3, '#3a7fbf');
  const jug = iso(0, 0, 36);
  pc.ellipse(jug.x, jug.y - 6, 8, 8, '#a9dcef');
  pc.ellipse(jug.x, jug.y - 13, 4, 2, '#8ac4dc');
};

const drawBathtub: Drawer = (pc, rotation) => {
  drawBox(pc, box(-0.48, 0.48, -0.4, 0.4, 0, 22, faces('#eef2f4', '#ffffff')));
  const top = iso(0, 0, 22);
  pc.ellipse(top.x, top.y, 14, 6, '#8ac4dc');
  pc.ellipse(top.x - 3, top.y - 1, 5, 2, '#e8f6fb');
  pc.ellipse(top.x + 4, top.y + 1, 4, 2, '#ffffff');
  // The faucet sits on whichever end faces the viewer.
  const dir = facing(rotation) === 'right' ? 1 : -1;
  pc.rect(top.x + dir * 10, top.y - 9, 2, 8, STEEL);
};

const drawToilet: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.2, 0.2, -0.16, 0.16, 0, 14, faces('#eef2f4', '#ffffff')));
  drawBox(pc, box(-0.22, 0.22, -0.1, 0.1, 14, 34, faces('#e4e8ea', '#f4f7f9')));
  const lid = iso(0.05, 0.05, 14);
  pc.ellipse(lid.x, lid.y, 7, 3, '#ffffff');
  const h = onFace(face, 0.1, 0.12, 30);
  pc.rect(h.x, h.y, 3, 1, STEEL);
};

const drawWashbasin: Drawer = (pc, rotation) => {
  const dir = facing(rotation) === 'right' ? 1 : -1;
  drawCylinder(pc, 0.14, 0, 22, '#e4e8ea', '#f4f7f9');
  const top = iso(0, 0, 30);
  pc.ellipse(top.x, top.y, 12, 5, '#eef2f4');
  pc.ellipse(top.x, top.y, 9, 3.5, '#8ac4dc');
  pc.rect(top.x - 1, top.y - 10, 2, 8, STEEL);
  // The tap handle sits on whichever side faces the viewer.
  pc.rect(top.x + dir * 3 - 1, top.y - 11, 2, 2, STEEL);
  pc.rect(top.x - 5, top.y - 34, 10, 22, '#c8e4ee');
  pc.rect(top.x - 5, top.y - 34, 10, 2, '#8a5a34');
};

const drawShower: Drawer = (pc, rotation) => {
  drawBox(pc, box(-0.44, 0.44, -0.44, 0.44, 0, 3, faces('#dfe4e8', '#f4f7f9')));
  const post = (u: number, v: number) =>
    drawBox(pc, box(u - 0.03, u + 0.03, v - 0.03, v + 0.03, 3, 66, faces(STEEL)));
  post(-0.44, -0.44);
  post(0.44, 0.44);
  post(0.44, -0.44);
  post(-0.44, 0.44);
  faceQuad(pc, 'left', 0.44, -0.4, 0.4, 5, 62, '#c8e8f430');
  faceQuad(pc, 'right', 0.44, -0.4, 0.4, 5, 62, '#a8d8ec30');
  // The showerhead is bolted to one corner post, which turns with the stall.
  const [hu, hv] = turn(-0.3, -0.3, rotation);
  const head = iso(hu, hv, 62);
  pc.rect(head.x - 4, head.y, 8, 2, STEEL);
  for (let i = 0; i < 3; i += 1) pc.rect(head.x - 3 + i * 3, head.y + 4 + i * 4, 1, 3, '#8ac4dc');
};

const drawWashingMachine: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.4, 0.4, -0.4, 0.4, 0, 30, faces('#e4e8ea', '#f4f7f9')));
  const dial = onFace(face, 0.4, -0.22, 26);
  pc.rect(dial.x, dial.y, 3, 2, '#e8503a');
  const door = onFace(face, 0.4, 0.05, 13);
  pc.ellipse(door.x, door.y, 8, 8, '#8a9298');
  pc.ellipse(door.x, door.y, 6, 6, '#5a8fb0');
  pc.ellipse(door.x - 1, door.y - 1, 2, 2, '#bfe8ff');
};

const drawLaundryRack: Drawer = (pc) => {
  pc.line(-14, 0, -12, -32, STEEL);
  pc.line(14, 0, 12, -32, STEEL);
  pc.rect(-13, -32, 26, 2, '#8a9298');
  pc.rect(-13, -22, 26, 1, '#8a9298');
  pc.rect(-10, -30, 6, 12, '#e8503a');
  pc.rect(-2, -30, 5, 9, '#5b9bd5');
  pc.rect(5, -30, 6, 14, '#f0bf36');
  pc.rect(-8, -20, 5, 8, '#ffffff');
};

/* -------------------------------------------------------------------------- */
/* Lighting                                                                   */
/* -------------------------------------------------------------------------- */

const drawArcLamp: Drawer = (pc, rotation) => {
  // The arm reaches toward whichever side faces the viewer.
  const s = facing(rotation) === 'left' ? -1 : 1;
  drawCylinder(pc, 0.2, 0, 3, '#4a4a52', '#34343c');
  pc.rect(s > 0 ? -8 : 6, -64, 2, 62, '#5a5a62');
  pc.line(s > 0 ? -7 : 7, -64, s * 4, -74, '#5a5a62');
  pc.line(s * 4, -74, s * 12, -66, '#5a5a62');
  pc.poly(
    [
      { x: s * 7, y: -66 },
      { x: s * 17, y: -66 },
      { x: s * 14, y: -60 },
      { x: s * 10, y: -60 },
    ],
    '#ffe58a',
  );
  pc.rect(s > 0 ? 9 : -15, -66, 6, 2, '#fff2b0');
};

const drawTableLamp: Drawer = (pc) => {
  drawCylinder(pc, 0.13, 0, 3, '#8a5a34', '#6a4028');
  pc.rect(-1, -14, 2, 12, '#8a5a34');
  pc.poly(
    [
      { x: -7, y: -14 },
      { x: 7, y: -14 },
      { x: 4, y: -24 },
      { x: -4, y: -24 },
    ],
    '#f3e2b3',
  );
  pc.poly(
    [
      { x: 2, y: -14 },
      { x: 7, y: -14 },
      { x: 4, y: -24 },
      { x: 2, y: -24 },
    ],
    '#dcc58f',
  );
};

const drawCandles: Drawer = (pc) => {
  drawCylinder(pc, 0.2, 0, 3, '#8a6a2a', '#6a4a1a');
  pc.rect(-1, -20, 2, 18, '#8a6a2a');
  pc.line(-1, -18, -9, -22, '#8a6a2a');
  pc.line(1, -18, 9, -22, '#8a6a2a');
  for (const [x, y] of [
    [0, -26],
    [-10, -28],
    [10, -28],
  ] as const) {
    pc.rect(x - 1, y, 3, 8, '#f8f2e0');
    pc.rect(x, y - 2, 1, 2, '#ffb84a');
  }
};

const drawPaperLantern: Drawer = (pc) => {
  drawCylinder(pc, 0.14, 0, 3, '#5a4030', '#40301f');
  pc.rect(-1, -50, 2, 48, '#8a6a2a');
  pc.line(0, -50, 8, -50, '#8a6a2a');
  pc.rect(8, -50, 1, 4, '#8a6a2a');
  pc.ellipse(8, -56, 8, 9, '#d9433b');
  pc.ellipse(6, -58, 3, 4, '#ee6a5a');
  pc.rect(0, -58, 16, 1, '#f0bf36');
  pc.rect(5, -68, 6, 2, '#f0bf36');
  pc.rect(5, -46, 6, 2, '#f0bf36');
  pc.rect(7, -44, 2, 5, '#f0bf36');
};

/* -------------------------------------------------------------------------- */
/* Walls and building                                                         */
/* -------------------------------------------------------------------------- */

function wallDrawer(
  height: number,
  body: string,
  options: { window?: boolean; opening?: boolean } = {},
): Drawer {
  return (pc, rotation) => {
    const colors = faces(body, shade(body, 1.12));
    if (options.opening) {
      // Two side posts and a lintel leave a gap to walk through.
      const span = alongU(rotation);
      const post = (side: -1 | 1) =>
        span
          ? box(0.3 * side - 0.12, 0.3 * side + 0.12, -0.09, 0.09, 0, height, colors)
          : box(-0.09, 0.09, 0.3 * side - 0.12, 0.3 * side + 0.12, 0, height, colors);
      drawBoxes(pc, [post(-1), post(1), slab(rotation, 0.09, height - 8, height, colors)]);
      return;
    }
    drawBox(pc, slab(rotation, 0.09, 0, height, colors));
    if (options.window) {
      slabFace(pc, rotation, 0.09, -0.32, 0.32, height * 0.35, height * 0.85, '#8ac4dc');
      slabFace(pc, rotation, 0.09, -0.32, 0.32, height * 0.35, height * 0.42, '#ffffffaa');
      slabFace(pc, rotation, 0.09, -0.02, 0.02, height * 0.35, height * 0.85, WHITE);
      slabFace(pc, rotation, 0.09, -0.34, 0.34, height * 0.33, height * 0.36, WOOD_DARK);
    }
    // A skirting board along the foot of every solid wall.
    slabFace(pc, rotation, 0.09, -0.5, 0.5, 0, 3, shade(body, 0.7));
  };
}

/** A wall with a built-in display shelf nook partway up. */
function wallShelfDrawer(height: number, body: string): Drawer {
  return (pc, rotation) => {
    const colors = faces(body, shade(body, 1.12));
    drawBox(pc, slab(rotation, 0.09, 0, height, colors));
    slabFace(pc, rotation, 0.09, -0.5, 0.5, 0, 3, shade(body, 0.7));
    slabFace(pc, rotation, 0.1, -0.22, 0.22, height * 0.42, height * 0.58, shade(body, 0.55));
    slabFace(pc, rotation, 0.1, -0.18, -0.06, height * 0.46, height * 0.5, '#c9a24a');
    slabFace(pc, rotation, 0.1, 0.02, 0.16, height * 0.46, height * 0.5, '#8ac4dc');
  };
}

/** A wall opening hung with a beaded curtain instead of a bare gap. */
function curtainDoorwayDrawer(height: number, body: string): Drawer {
  const colors = faces(body, shade(body, 1.12));
  const strand = '#c94f5c';
  const bead = '#f0c23a';
  return (pc, rotation) => {
    const span = alongU(rotation);
    const post = (side: -1 | 1) =>
      span
        ? box(0.3 * side - 0.12, 0.3 * side + 0.12, -0.09, 0.09, 0, height, colors)
        : box(-0.09, 0.09, 0.3 * side - 0.12, 0.3 * side + 0.12, 0, height, colors);
    drawBoxes(pc, [post(-1), post(1), slab(rotation, 0.09, height - 8, height, colors)]);
    for (let i = -3; i <= 3; i += 1) {
      const s = i * 0.065;
      const drop = i % 2 === 0 ? 24 : 18;
      const top = span ? iso(s, 0, height - 10) : iso(0, s, height - 10);
      const bottom = span ? iso(s, 0, height - 10 - drop) : iso(0, s, height - 10 - drop);
      pc.line(top.x, top.y, bottom.x, bottom.y, strand);
      pc.px(bottom.x, bottom.y, bead);
    }
  };
}

/** A wall with a round porthole window, in place of `wallDrawer`'s square one. */
function windowRoundDrawer(height: number, body: string): Drawer {
  return (pc, rotation) => {
    const colors = faces(body, shade(body, 1.12));
    drawBox(pc, slab(rotation, 0.09, 0, height, colors));
    slabFace(pc, rotation, 0.09, -0.5, 0.5, 0, 3, shade(body, 0.7));
    const center = onFace(facing(rotation), 0.1, 0, height * 0.6);
    pc.ellipse(center.x, center.y, 11, 11, WOOD_DARK);
    pc.ellipse(center.x, center.y, 9, 9, '#8ac4dc');
    pc.ellipse(center.x - 2, center.y - 2, 3, 3, '#ffffffaa');
  };
}

/** A wall with an arched-top window, a fancier sibling of `wallDrawer`'s window. */
function wallArchWindowDrawer(height: number, body: string): Drawer {
  return (pc, rotation) => {
    const colors = faces(body, shade(body, 1.12));
    drawBox(pc, slab(rotation, 0.09, 0, height, colors));
    slabFace(pc, rotation, 0.09, -0.5, 0.5, 0, 3, shade(body, 0.7));
    slabFace(pc, rotation, 0.09, -0.28, 0.28, height * 0.3, height * 0.75, '#8ac4dc');
    slabFace(pc, rotation, 0.09, -0.28, 0.28, height * 0.3, height * 0.37, '#ffffffaa');
    slabFace(pc, rotation, 0.09, -0.3, 0.3, height * 0.28, height * 0.32, WOOD_DARK);
    const cap = onFace(facing(rotation), 0.1, 0, height * 0.8);
    pc.ellipse(cap.x, cap.y, 10, 6, WOOD_DARK);
    pc.ellipse(cap.x, cap.y, 8, 4, '#8ac4dc');
  };
}

/** Square-column sibling of `drawPillar`. */
const drawPillarSquare: Drawer = (pc) => {
  drawBox(pc, box(-0.2, 0.2, -0.2, 0.2, 0, 4, faces('#d8d0bc')));
  drawBox(pc, box(-0.14, 0.14, -0.14, 0.14, 4, 66, faces('#efe8d4', '#d8d0bc')));
  drawBox(pc, box(-0.2, 0.2, -0.2, 0.2, 66, 72, faces('#d8d0bc')));
};

/** A freestanding folding screen: three narrow panels, shorter than a full wall. */
function roomDividerDrawer(height: number, body: string): Drawer {
  const colors = faces(body, shade(body, 1.1));
  return (pc, rotation) => {
    const along = alongU(rotation);
    const panels: BoxPiece[] = [-0.3, 0, 0.3].map((s) =>
      along
        ? box(s - 0.11, s + 0.11, -0.03, 0.03, 0, height, colors)
        : box(-0.03, 0.03, s - 0.11, s + 0.11, 0, height, colors),
    );
    drawBoxes(pc, panels);
    slabFace(pc, rotation, 0.03, -0.09, 0.09, height * 0.3, height * 0.7, '#f1eee680');
  };
}

/** A waist-height railing with balusters, for balconies and upper floors. */
function railingDrawer(post: string, rail: string): Drawer {
  return (pc, rotation) => {
    const along = alongU(rotation);
    const posts: BoxPiece[] = [-0.42, -0.21, 0, 0.21, 0.42].map((s) =>
      along
        ? box(s - 0.025, s + 0.025, -0.03, 0.03, 0, 30, faces(post))
        : box(-0.03, 0.03, s - 0.025, s + 0.025, 0, 30, faces(post)),
    );
    drawBoxes(pc, [...posts, slab(rotation, 0.03, 0, 3, faces(shade(post, 0.85))), slab(rotation, 0.03, 27, 30, faces(rail))]);
  };
}

/**
 * A genuinely see-through pane in a steel frame: unlike `wallDrawer`, its
 * body faces use alpha-blended colour instead of `faces()` (whose `shade()`
 * step would strip any alpha), so whatever is behind it -- floor, other
 * furniture -- actually shows through in the rendered scene.
 */
const drawGlassWall: Drawer = (pc, rotation) => {
  const height = 66;
  const thickness = 0.07;
  const { u0, u1, v0, v1, h0, h1 } = slab(rotation, thickness, 0, height, faces('#000000'));
  alphaBox(pc, u0, u1, v0, v1, h0, h1);
  // Steel top and bottom rails keep it readable as a standing panel.
  slabFace(pc, rotation, thickness, -0.5, 0.5, 0, 3, shade(STEEL, 0.85));
  slabFace(pc, rotation, thickness, -0.5, 0.5, height - 3, height, STEEL);
  // A diagonal sheen, like light catching real glass.
  slabFace(pc, rotation, thickness, -0.32, -0.08, height * 0.15, height * 0.85, '#ffffff33');
};

/**
 * Which quadrant an L-corner's two arms reach into. Unlike a straight wall
 * (which only has two meaningful facings, since a symmetric slab looks the
 * same rotated 180 degrees), a corner needs all four rotations distinct --
 * one per corner of a rectangular room.
 */
function cornerQuadrant(rotation: number): { su: 1 | -1; sv: 1 | -1 } {
  return { su: rotation === 0 || rotation === 2 ? 1 : -1, sv: rotation === 0 || rotation === 6 ? 1 : -1 };
}

/** A solid L-shaped corner: a post at the pivot, with an arm reaching to each of the two tile edges it joins. */
function cornerWallDrawer(height: number, body: string): Drawer {
  const colors = faces(body, shade(body, 1.12));
  return (pc, rotation) => {
    const t = 0.09;
    const { su, sv } = cornerQuadrant(rotation);
    const post = box(-t, t, -t, t, 0, height, colors);
    const armU = su > 0 ? box(t, 0.5, -t, t, 0, height, colors) : box(-0.5, -t, -t, t, 0, height, colors);
    const armV = sv > 0 ? box(-t, t, t, 0.5, 0, height, colors) : box(-t, t, -0.5, -t, 0, height, colors);
    drawBoxes(pc, [post, armU, armV]);
  };
}

/** One alpha-blended box face set, the same technique as `drawGlassWall`, factored out for reuse by the corner. */
function alphaBox(pc: PixelCanvas, u0: number, u1: number, v0: number, v1: number, h0: number, h1: number): void {
  pc.poly([iso(u0, v1, h0), iso(u1, v1, h0), iso(u1, v1, h1), iso(u0, v1, h1)], '#bfe9f75c');
  pc.poly([iso(u1, v0, h0), iso(u1, v1, h0), iso(u1, v1, h1), iso(u1, v0, h1)], '#8fc3d64c');
  pc.poly([iso(u0, v0, h1), iso(u1, v0, h1), iso(u1, v1, h1), iso(u0, v1, h1)], '#eef9ff70');
}

/** The see-through counterpart to `cornerWallDrawer`, matching `drawGlassWall`'s pane. */
const drawGlassCorner: Drawer = (pc, rotation) => {
  const height = 66;
  const t = 0.07;
  const { su, sv } = cornerQuadrant(rotation);
  const post: [number, number, number, number] = [-t, t, -t, t];
  const armU: [number, number, number, number] = su > 0 ? [t, 0.5, -t, t] : [-0.5, -t, -t, t];
  const armV: [number, number, number, number] = sv > 0 ? [-t, t, t, 0.5] : [-t, t, -0.5, -t];
  for (const [u0, u1, v0, v1] of [post, armU, armV]) alphaBox(pc, u0, u1, v0, v1, 0, height);
};

const drawPillar: Drawer = (pc) => {
  drawBox(pc, box(-0.2, 0.2, -0.2, 0.2, 0, 4, faces('#d8d0bc')));
  drawCylinder(pc, 0.14, 4, 66, '#efe8d4', '#d8d0bc');
  drawBox(pc, box(-0.2, 0.2, -0.2, 0.2, 66, 72, faces('#d8d0bc')));
};

/**
 * A door: two frame posts and a lintel always stand; the leaf between them
 * either fills the gap (closed, blocks like a wall) or tucks edge-on against
 * a post (open, walkable) -- see `packages/protocol` furniture/home.ts.
 * `glass` swaps the leaf for a genuinely see-through pane, the same
 * alpha-blended technique as `drawGlassWall`.
 */
function doorDrawer(height: number, body: string, options: { glass?: boolean } = {}): Drawer {
  const frame = faces(shade(body, 0.8), shade(body, 1.05));
  return (pc, rotation, open) => {
    const span = alongU(rotation);
    const post = (side: -1 | 1) =>
      span
        ? box(0.3 * side - 0.12, 0.3 * side + 0.12, -0.09, 0.09, 0, height, frame)
        : box(-0.09, 0.09, 0.3 * side - 0.12, 0.3 * side + 0.12, 0, height, frame);
    const lintel = slab(rotation, 0.09, height - 8, height, frame);

    if (open) {
      const edge = span
        ? box(0.16, 0.22, -0.07, 0.07, 2, height - 10, faces(shade(body, 0.75)))
        : box(-0.07, 0.07, 0.16, 0.22, 2, height - 10, faces(shade(body, 0.75)));
      drawBoxes(pc, [post(-1), post(1), edge, lintel]);
      return;
    }

    drawBoxes(pc, [post(-1), post(1), lintel]);
    if (options.glass) {
      const { u0, u1, v0, v1, h0, h1 } = span
        ? box(-0.16, 0.16, -0.05, 0.05, 1, height - 9, frame)
        : box(-0.05, 0.05, -0.16, 0.16, 1, height - 9, frame);
      pc.poly([iso(u0, v1, h0), iso(u1, v1, h0), iso(u1, v1, h1), iso(u0, v1, h1)], '#bfe9f75c');
      pc.poly([iso(u1, v0, h0), iso(u1, v1, h0), iso(u1, v1, h1), iso(u1, v0, h1)], '#8fc3d64c');
      pc.poly([iso(u0, v0, h1), iso(u1, v0, h1), iso(u1, v1, h1), iso(u0, v1, h1)], '#eef9ff70');
    } else {
      const leaf = span
        ? box(-0.16, 0.16, -0.06, 0.06, 1, height - 9, faces(body, shade(body, 1.18)))
        : box(-0.06, 0.06, -0.16, 0.16, 1, height - 9, faces(body, shade(body, 1.18)));
      drawBox(pc, leaf);
      // A small knob near the leading edge.
      const knob = span ? iso(0.1, 0, height * 0.42) : iso(0, 0.1, height * 0.42);
      pc.rect(knob.x - 1, knob.y - 1, 2, 3, shade(body, 0.35));
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Garden                                                                     */
/* -------------------------------------------------------------------------- */

function speckled(base: string, dark: string, light: string, seed: number, count: number): Drawer {
  return (pc) => {
    flatDiamond(pc, 0.5, base);
    const rand = seededRandom(seed);
    for (let i = 0; i < count; i += 1) {
      const p = iso((rand() - 0.5) * 0.9, (rand() - 0.5) * 0.9);
      pc.px(p.x, p.y, rand() > 0.5 ? dark : light);
    }
  };
}

const drawLawn = speckled('#5fb85a', '#3f9a44', '#86d27a', 21, 40);

const drawGardenPath: Drawer = (pc) => {
  flatDiamond(pc, 0.5, '#6fb562');
  const stones: Array<[number, number, number]> = [
    [-0.22, -0.2, 0.17],
    [0.2, -0.18, 0.15],
    [-0.18, 0.22, 0.15],
    [0.22, 0.2, 0.17],
    [0, 0, 0.14],
  ];
  for (const [u, v, r] of stones) {
    const c = iso(u, v);
    pc.ellipse(c.x, c.y, r * HW * 1.4, r * HH * 1.4, '#b9b4a6');
    pc.ellipse(c.x - 1, c.y - 1, r * HW, r * HH, '#d6d1c2');
  }
};

const drawGardenDeck: Drawer = (pc) => {
  flatDiamond(pc, 0.5, '#b98a5c');
  for (let i = -3; i <= 3; i += 1) {
    const a = iso(-0.48, i * 0.14);
    const b = iso(0.48, i * 0.14);
    pc.line(a.x, a.y, b.x, b.y, '#8a5a34');
  }
  const a = iso(-0.5, -0.5);
  const b = iso(0.5, -0.5);
  pc.line(a.x, a.y, b.x, b.y, '#d3a574');
};

function fenceDrawer(color: string, top: string): Drawer {
  return (pc, rotation) => {
    const posts: BoxPiece[] = [];
    const along = alongU(rotation);
    for (const s of [-0.4, -0.13, 0.13, 0.4]) {
      posts.push(
        along
          ? box(s - 0.05, s + 0.05, -0.05, 0.05, 0, 26, faces(color, top))
          : box(-0.05, 0.05, s - 0.05, s + 0.05, 0, 26, faces(color, top)),
      );
    }
    drawBoxes(pc, [
      ...posts,
      slab(rotation, 0.025, 8, 12, faces(shade(color, 0.9))),
      slab(rotation, 0.025, 18, 22, faces(shade(color, 0.9))),
    ]);
  };
}

/** An L-shaped corner counterpart to `fenceDrawer`, using the same post-and-rail layout as `cornerWallDrawer`. */
function cornerFenceDrawer(color: string, top: string): Drawer {
  const postColors = faces(color, top);
  const railColor = faces(shade(color, 0.9));
  return (pc, rotation) => {
    const { su, sv } = cornerQuadrant(rotation);
    const post = (u: number, v: number) => box(u - 0.05, u + 0.05, v - 0.05, v + 0.05, 0, 26, postColors);
    const posts: BoxPiece[] = [post(0, 0)];
    for (const s of [0.13, 0.4]) {
      posts.push(post(su * s, 0));
      posts.push(post(0, sv * s));
    }
    const armU: [number, number, number, number] = su > 0 ? [0, 0.5, -0.025, 0.025] : [-0.5, 0, -0.025, 0.025];
    const armV: [number, number, number, number] = sv > 0 ? [-0.025, 0.025, 0, 0.5] : [-0.025, 0.025, -0.5, 0];
    drawBoxes(pc, [
      ...posts,
      ...[armU, armV].flatMap(([u0, u1, v0, v1]) => [
        box(u0, u1, v0, v1, 8, 12, railColor),
        box(u0, u1, v0, v1, 18, 22, railColor),
      ]),
    ]);
  };
}

const drawHedge: Drawer = (pc) => {
  drawBox(pc, box(-0.46, 0.46, -0.4, 0.4, 0, 30, faces(LEAF_DARK, LEAF)));
  const rand = seededRandom(5);
  for (let i = 0; i < 22; i += 1) {
    const p = iso((rand() - 0.5) * 0.85, (rand() - 0.5) * 0.85, 30);
    pc.px(p.x, p.y, rand() > 0.5 ? LEAF_LIGHT : '#2a6f34');
  }
  faceQuad(pc, 'left', 0.4, -0.3, 0.3, 14, 18, LEAF);
};

const drawFlowerBed: Drawer = (pc) => {
  drawBox(pc, box(-0.44, 0.44, -0.44, 0.44, 0, 8, faces('#8a5a34', '#5a3a20')));
  const rand = seededRandom(9);
  const colors = ['#ff5a8a', '#ffd24a', '#ffffff', '#ff8a3a', '#b57aff'];
  for (let i = 0; i < 14; i += 1) {
    const p = iso((rand() - 0.5) * 0.8, (rand() - 0.5) * 0.8, 8);
    pc.rect(p.x, p.y - 4, 1, 4, LEAF);
    pc.rect(p.x - 1, p.y - 6, 3, 3, colors[i % colors.length]!);
  }
};

const drawFrangipani: Drawer = (pc) => {
  drawCylinder(pc, 0.06, 0, 4, '#6a4a30', '#5a3a20');
  pc.poly(
    [
      { x: -3, y: -3 },
      { x: 3, y: -3 },
      { x: 2, y: -34 },
      { x: -2, y: -34 },
    ],
    '#8a6a4a',
  );
  pc.line(0, -30, -12, -44, '#8a6a4a');
  pc.line(0, -30, 12, -46, '#8a6a4a');
  for (const [x, y, r] of [
    [-14, -50, 10],
    [12, -52, 10],
    [0, -58, 12],
    [-4, -46, 9],
    [6, -44, 8],
  ] as const) {
    pc.ellipse(x, y, r, r * 0.75, LEAF);
    pc.ellipse(x + 2, y - 2, r * 0.6, r * 0.45, LEAF_LIGHT);
  }
  for (const [x, y] of [
    [-10, -54],
    [8, -58],
    [-2, -62],
    [14, -48],
    [-16, -46],
  ] as const) {
    pc.rect(x - 1, y, 3, 3, '#ffffff');
    pc.px(x, y + 1, '#ffd24a');
  }
};

const drawPalmTree: Drawer = (pc) => {
  drawCylinder(pc, 0.1, 0, 3, '#6a4a30', '#5a3a20');
  for (let i = 0; i < 6; i += 1) {
    const x = 2 + Math.sin(i * 0.5) * 2 - i * 0.5;
    pc.rect(x - 2, -6 - i * 9, 4, 10, i % 2 ? '#8a6a4a' : '#9a7a58');
  }
  const top = { x: -2, y: -62 };
  const fronds: Array<[number, number]> = [
    [-26, 10],
    [-22, -2],
    [-10, -12],
    [10, -12],
    [24, -2],
    [28, 12],
    [0, -16],
  ];
  for (const [dx, dy] of fronds) {
    pc.poly(
      [
        { x: top.x, y: top.y },
        { x: top.x + dx * 0.6, y: top.y + dy - 6 },
        { x: top.x + dx, y: top.y + dy + 6 },
        { x: top.x + dx * 0.3, y: top.y + 3 },
      ],
      LEAF,
    );
    pc.line(top.x, top.y, top.x + dx, top.y + dy, LEAF_LIGHT);
  }
  pc.ellipse(top.x - 3, top.y + 5, 3, 3, '#7a5a30');
  pc.ellipse(top.x + 4, top.y + 6, 3, 3, '#8a6a3a');
};

const drawGardenBench: Drawer = (pc, rotation) => {
  for (const [u, v] of [
    [-0.4, -0.3],
    [0.4, -0.3],
    [0.4, 0.3],
    [-0.4, 0.3],
  ] as const) {
    drawBox(pc, box(u - 0.04, u + 0.04, v - 0.04, v + 0.04, 0, 12, faces('#3a3a44')));
  }
  drawBoxes(
    pc,
    seatWithBack(
      rotation,
      0.46,
      [12, 16],
      34,
      0.05,
      faces('#7a9a5a', '#98b878'),
      faces('#5a7a3a', '#7a9a5a'),
    ),
  );
};

const drawPond: Drawer = (pc) => {
  drawBox(pc, box(-0.48, 0.48, -0.48, 0.48, 0, 8, faces('#8a8478', '#a09a8c')));
  const c = iso(0, 0, 8);
  pc.ellipse(c.x, c.y, 24, 11, '#3a7fbf');
  pc.ellipse(c.x - 2, c.y - 1, 20, 8, '#5bb0d8');
  pc.ellipse(c.x - 8, c.y + 1, 5, 2, LEAF);
  pc.ellipse(c.x + 8, c.y - 2, 4, 2, LEAF_LIGHT);
  pc.rect(c.x + 5, c.y - 4, 3, 1, '#ff8a3a');
  pc.px(c.x + 8, c.y - 4, '#ff8a3a');
  pc.rect(c.x - 6, c.y + 2, 3, 1, '#ffffff');
  pc.px(c.x - 3, c.y + 2, '#ff8a3a');
  pc.px(c.x - 14, c.y - 3, '#ffffff88');
};

const drawGardenLamp: Drawer = (pc) => {
  drawCylinder(pc, 0.1, 0, 3, '#2a2a32', '#1a1a22');
  pc.rect(-1, -44, 2, 42, '#2a2a32');
  pc.poly(
    [
      { x: -6, y: -44 },
      { x: 6, y: -44 },
      { x: 6, y: -56 },
      { x: -6, y: -56 },
    ],
    '#ffe58a',
  );
  pc.rect(-7, -58, 14, 3, '#2a2a32');
  pc.rect(-2, -62, 4, 4, '#2a2a32');
  pc.rect(-6, -46, 12, 2, '#2a2a32');
  pc.rect(-3, -54, 3, 8, '#fff2b0');
};

const drawBbqPit: Drawer = (pc, rotation) => {
  // The pit is a rectangle, not a square, so it has to swap axes with facing.
  const long = alongU(rotation);
  drawBox(pc, long ? box(-0.4, 0.4, -0.3, 0.3, 0, 20, faces('#3a3a44', '#55555f')) : box(-0.3, 0.3, -0.4, 0.4, 0, 20, faces('#3a3a44', '#55555f')));
  const top = iso(0, 0, 20);
  const rx = long ? 12 : 8;
  const ry = long ? 5 : 8;
  pc.ellipse(top.x, top.y, rx, ry, '#1a1a22');
  pc.ellipse(top.x, top.y - 1, rx - 2, ry - 1, '#e8503a');
  for (let i = -3; i <= 3; i++) pc.rect(top.x + i * (long ? 3 : 1), top.y - 4, 1, 7, '#9aa2a8');
  pc.rect(top.x - 6, top.y - 6, 12, 2, '#b5573f');
  pc.rect(top.x - 4, top.y - 8, 8, 2, '#d9433b');
};

const drawHammock: Drawer = (pc, rotation) => {
  const long = alongU(rotation);
  const pt = (u: number, v: number, h: number) => (long ? iso(u, v, h) : iso(v, u, h));
  drawBox(pc, long ? box(-0.5, -0.4, -0.06, 0.06, 0, 40, faces(WOOD_DARK)) : box(-0.06, 0.06, -0.5, -0.4, 0, 40, faces(WOOD_DARK)));
  drawBox(pc, long ? box(0.4, 0.5, -0.06, 0.06, 0, 40, faces(WOOD_DARK)) : box(-0.06, 0.06, 0.4, 0.5, 0, 40, faces(WOOD_DARK)));
  const a = pt(-0.45, 0, 38);
  const b = pt(0.45, 0, 38);
  pc.poly([a, b, pt(0.3, 0, 16), pt(-0.3, 0, 16)], '#e8703a');
  pc.line(a.x, a.y, pt(-0.3, 0, 16).x, pt(-0.3, 0, 16).y, '#d9433b');
  pc.line(b.x, b.y, pt(0.3, 0, 16).x, pt(0.3, 0, 16).y, '#d9433b');
  const mid = pt(0, 0, 14);
  pc.ellipse(mid.x, mid.y, long ? 12 : 3, long ? 3 : 12, '#f08a58');
};

const drawUmbrellaTable: Drawer = (pc) => {
  drawCylinder(pc, 0.05, 0, 26, '#8a8a92', '#6a6a72');
  drawCylinder(pc, 0.4, 24, 27, '#efe8d4', '#8a8a92');
  pc.rect(-1, -66, 2, 42, '#8a5a34');
  pc.poly(
    [
      { x: -26, y: -52 },
      { x: 26, y: -52 },
      { x: 14, y: -68 },
      { x: -14, y: -68 },
    ],
    '#e8503a',
  );
  pc.poly(
    [
      { x: -8, y: -52 },
      { x: 8, y: -52 },
      { x: 6, y: -68 },
      { x: -6, y: -68 },
    ],
    '#ffffff',
  );
  pc.rect(-1, -72, 2, 4, '#8a5a34');
};

const drawGnome: Drawer = (pc, rotation) => {
  // A small lean toward whichever side faces the viewer, so it isn't a flat mirror image.
  const lean = frontFace(rotation) === 'right' ? 2 : -2;
  pc.ellipse(lean, -6, 6, 6, '#3a6fd6');
  pc.ellipse(lean, -13, 4, 3, '#f0c8a0');
  pc.poly(
    [
      { x: lean - 5, y: -14 },
      { x: lean + 5, y: -14 },
      { x: lean, y: -28 },
    ],
    '#d9433b',
  );
  pc.rect(lean - 3, -12, 6, 3, '#f4f0e4');
  pc.px(lean - 1, -14, '#2a1e19');
  pc.px(lean + 1, -14, '#2a1e19');
  pc.rect(lean - 4, -2, 3, 2, '#5a3a20');
  pc.rect(lean + 1, -2, 3, 2, '#5a3a20');
};

const drawBirdBath: Drawer = (pc) => {
  drawCylinder(pc, 0.2, 0, 3, '#b9b4a6', '#8a8478');
  drawCylinder(pc, 0.06, 3, 24, '#d6d1c2', '#a09a8c');
  drawCylinder(pc, 0.34, 24, 28, '#d6d1c2', '#a09a8c');
  const c = iso(0, 0, 28);
  pc.ellipse(c.x, c.y, 8, 3, '#8ac4dc');
  pc.rect(c.x + 2, c.y - 6, 4, 4, '#8a6a4a');
  pc.px(c.x + 5, c.y - 5, '#f0bf36');
  pc.px(c.x + 3, c.y - 5, '#2a1e19');
};

const drawWishingWell: Drawer = (pc) => {
  drawCylinder(pc, 0.4, 0, 20, '#8a8478', '#a09a8c');
  const c = iso(0, 0, 20);
  pc.ellipse(c.x, c.y, 11, 5, '#3a7fbf');
  drawBox(pc, box(-0.38, -0.32, -0.04, 0.04, 20, 54, faces(WOOD_DARK)));
  drawBox(pc, box(0.32, 0.38, -0.04, 0.04, 20, 54, faces(WOOD_DARK)));
  pc.poly(
    [
      { x: -24, y: -54 },
      { x: 24, y: -54 },
      { x: 0, y: -68 },
    ],
    '#b5573f',
  );
  pc.poly(
    [
      { x: 0, y: -54 },
      { x: 24, y: -54 },
      { x: 0, y: -68 },
    ],
    '#8a3a2a',
  );
  pc.rect(-16, -52, 32, 2, '#8a6a4a');
  pc.rect(-1, -52, 2, 10, '#c9a878');
  pc.rect(-3, -42, 6, 5, '#8a5a34');
};

const drawScarecrow: Drawer = (pc, rotation) => {
  // A small lean toward whichever side faces the viewer, so it isn't a flat mirror image.
  const lean = frontFace(rotation) === 'right' ? 2 : -2;
  pc.rect(-1 + lean, -44, 2, 44, '#8a5a34');
  pc.rect(-16 + lean, -34, 32, 2, '#8a5a34');
  pc.rect(-6 + lean, -36, 12, 14, '#3a6fd6');
  pc.rect(-6 + lean, -26, 12, 4, '#f0bf36');
  pc.ellipse(lean, -42, 6, 6, '#e8c88a');
  pc.px(lean - 2, -43, '#2a1e19');
  pc.px(lean + 2, -43, '#2a1e19');
  pc.rect(lean - 2, -39, 5, 1, '#8a3a2a');
  pc.rect(lean - 9, -47, 18, 3, '#d8b45a');
  pc.rect(lean - 5, -55, 10, 8, '#d8b45a');
  pc.rect(lean - 5, -49, 10, 2, '#d9433b');
  for (const x of [-20, 16]) {
    pc.rect(x + lean, -34, 4, 1, '#d8b45a');
    pc.rect(x + 1 + lean, -31, 3, 1, '#d8b45a');
  }
};

/* -------------------------------------------------------------------------- */
/* Farm                                                                       */
/* -------------------------------------------------------------------------- */

const drawGardenPlot: Drawer = (pc) => {
  flatDiamond(pc, 0.5, '#6a4a30');
  flatDiamond(pc, 0.44, '#7a5638');
  for (let i = -2; i <= 2; i += 1) {
    const a = iso(-0.4, i * 0.18);
    const b = iso(0.4, i * 0.18);
    pc.line(a.x, a.y, b.x, b.y, '#4a3020');
    pc.line(a.x, a.y - 1, b.x, b.y - 1, '#8a6642');
  }
};

const drawRaisedBed: Drawer = (pc) => {
  drawBox(pc, box(-0.48, 0.48, -0.48, 0.48, 0, 10, faces('#b98a5c', '#8a5a34')));
  const inner = (r: number, color: string) =>
    pc.poly([iso(-r, -r, 10), iso(r, -r, 10), iso(r, r, 10), iso(-r, r, 10)], color);
  inner(0.42, '#5a3a20');
  inner(0.36, '#7a5638');
  for (let i = -2; i <= 2; i += 1) {
    const a = iso(-0.32, i * 0.15, 10);
    const b = iso(0.32, i * 0.15, 10);
    pc.line(a.x, a.y, b.x, b.y, '#4a3020');
  }
};

const drawGreenhouseBed: Drawer = (pc, rotation) => {
  drawBox(pc, box(-0.48, 0.48, -0.48, 0.48, 0, 8, faces('#8a8478', '#a09a8c')));
  const inner = (r: number, color: string) =>
    pc.poly([iso(-r, -r, 8), iso(r, -r, 8), iso(r, r, 8), iso(-r, r, 8)], color);
  inner(0.42, '#6a4a30');
  const post = (u: number, v: number) =>
    drawBox(pc, box(u - 0.03, u + 0.03, v - 0.03, v + 0.03, 8, 40, faces('#dfe4e8')));
  post(-0.45, -0.45);
  post(0.45, -0.45);
  post(-0.45, 0.45);
  post(0.45, 0.45);
  faceQuad(pc, 'left', 0.45, -0.42, 0.42, 10, 38, '#c8e8f455');
  faceQuad(pc, 'right', 0.45, -0.42, 0.42, 10, 38, '#a8d8ec55');
  drawBox(pc, box(-0.5, 0.5, -0.5, 0.5, 40, 43, faces('#dfe4e8', '#f4f7f9')));
  // The roof vent sits on whichever eave faces the viewer.
  const vent = onFace(facing(rotation), 0.5, 0, 43);
  pc.rect(vent.x - 6, vent.y - 1, 12, 1, '#a8d8ec');
};

const drawChickenCoop: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.44, 0.44, -0.4, 0.4, 8, 30, faces('#c8573f', '#dc6f55')));
  for (const [u, v] of [
    [-0.4, -0.35],
    [0.4, -0.35],
    [0.4, 0.35],
    [-0.4, 0.35],
  ] as const) {
    drawBox(pc, box(u - 0.04, u + 0.04, v - 0.04, v + 0.04, 0, 8, faces(WOOD_DARK)));
  }
  pc.poly(
    [iso(-0.5, -0.46, 30), iso(0.5, -0.46, 30), iso(0.5, 0, 42), iso(-0.5, 0, 42)],
    '#8a5a34',
  );
  pc.poly([iso(-0.5, 0.46, 30), iso(0.5, 0.46, 30), iso(0.5, 0, 42), iso(-0.5, 0, 42)], '#a67849');
  faceQuad(pc, face, 0.4, -0.12, 0.12, 10, 22, '#2a1a12');
  const hen = onFace(face, 0.4, 0.3, 4);
  pc.ellipse(hen.x, hen.y - 4, 5, 4, '#fdfaf0');
  pc.ellipse(hen.x + 3, hen.y - 8, 2, 2, '#fdfaf0');
  pc.rect(hen.x + 4, hen.y - 8, 2, 1, '#f0a020');
  pc.rect(hen.x + 3, hen.y - 11, 1, 2, '#d9433b');
};

const drawBeehive: Drawer = (pc) => {
  drawBox(pc, box(-0.3, 0.3, -0.3, 0.3, 0, 6, faces('#8a5a34')));
  for (let i = 0; i < 3; i += 1) {
    drawBox(
      pc,
      box(
        -0.28,
        0.28,
        -0.28,
        0.28,
        6 + i * 9,
        14 + i * 9,
        faces(i % 2 ? '#e8b83a' : '#f0c850', '#f8dc80'),
      ),
    );
  }
  drawBox(pc, box(-0.32, 0.32, -0.32, 0.32, 33, 37, faces('#8a5a34', '#a67849')));
  const hole = iso(0.1, 0.28, 12);
  pc.rect(hole.x, hole.y, 4, 2, '#2a1a12');
  pc.px(hole.x - 4, hole.y - 8, '#2a1e19');
  pc.px(hole.x - 5, hole.y - 8, '#f0bf36');
  pc.px(hole.x + 8, hole.y - 14, '#2a1e19');
  pc.px(hole.x + 9, hole.y - 14, '#f0bf36');
};

const drawToolShed: Drawer = (pc, rotation) => {
  const face = facing(rotation);
  drawBox(pc, box(-0.46, 0.46, -0.42, 0.42, 0, 44, faces('#a67849', '#c08e5a')));
  pc.poly(
    [iso(-0.5, -0.46, 44), iso(0.5, -0.46, 44), iso(0.5, 0, 58), iso(-0.5, 0, 58)],
    '#5a6a7a',
  );
  pc.poly([iso(-0.5, 0.46, 44), iso(0.5, 0.46, 44), iso(0.5, 0, 58), iso(-0.5, 0, 58)], '#7a8a9a');
  faceQuad(pc, face, 0.42, -0.2, 0.2, 2, 34, '#5a3a20');
  faceQuad(pc, face, 0.42, -0.2, 0.2, 2, 34, '#6a4a30');
  faceQuad(pc, face, 0.42, -0.02, 0.02, 2, 34, '#3a2a18');
  const knob = onFace(face, 0.43, 0.14, 18);
  pc.rect(knob.x, knob.y, 2, 2, '#f0bf36');
  const spade = onFace(face, 0.43, -0.34, 4);
  pc.rect(spade.x, spade.y - 30, 1, 30, '#8a5a34');
  pc.rect(spade.x - 2, spade.y - 34, 5, 5, STEEL);
};

const drawWheelbarrow: Drawer = (pc, rotation) => {
  // The handles and wheel are on one side; mirror the whole thing to face the viewer.
  const mirror = facing(rotation) === 'left';
  const s = mirror ? -1 : 1;
  const mx = (x: number) => s * x;
  pc.poly(
    [
      { x: mx(-14), y: -14 },
      { x: mx(12), y: -14 },
      { x: mx(8), y: -4 },
      { x: mx(-8), y: -4 },
    ],
    '#3a8f5c',
  );
  pc.poly(
    [
      { x: mx(4), y: -14 },
      { x: mx(12), y: -14 },
      { x: mx(8), y: -4 },
      { x: mx(2), y: -4 },
    ],
    '#2a7048',
  );
  pc.ellipse(0, -14, 13, 3, '#6a4a30');
  pc.ellipse(mx(-12), -3, 4, 4, '#2a2a32');
  pc.ellipse(mx(-12), -3, 2, 2, '#9aa2a8');
  pc.line(mx(8), -12, mx(20), -18, '#8a5a34');
  pc.line(mx(8), -6, mx(20), -14, '#8a5a34');
  pc.rect(mirror ? 6 : -8, -3, 2, 3, '#8a5a34');
};

const drawHayBale: Drawer = (pc) => {
  drawBox(pc, box(-0.4, 0.4, -0.36, 0.36, 0, 18, faces('#d8b45a', '#ecd078')));
  faceQuad(pc, 'left', 0.36, -0.4, 0.4, 5, 6, '#a88a3a');
  faceQuad(pc, 'left', 0.36, -0.4, 0.4, 12, 13, '#a88a3a');
  faceQuad(pc, 'right', 0.4, -0.36, 0.36, 5, 6, '#a88a3a');
  faceQuad(pc, 'right', 0.4, -0.36, 0.36, 12, 13, '#a88a3a');
  const rand = seededRandom(3);
  for (let i = 0; i < 10; i += 1) {
    const p = iso((rand() - 0.5) * 0.8, (rand() - 0.5) * 0.7, 18);
    pc.rect(p.x, p.y - 2, 1, 3, '#ecd078');
  }
};

const drawWaterBarrel: Drawer = (pc) => {
  drawCylinder(pc, 0.3, 0, 30, '#8a6a4a', '#6a4a30');
  const c = iso(0, 0, 30);
  pc.ellipse(c.x, c.y, 11, 5, '#3a7fbf');
  for (const h of [5, 24]) {
    const a = iso(0, 0, h);
    pc.rect(a.x - 13, a.y + 2, 26, 2, '#3a3a44');
  }
  const t = iso(0.3, 0, 8);
  pc.rect(t.x, t.y, 4, 2, STEEL);
};

export const HOME_DRAWERS: Record<string, FurniDrawer> = {
  sofa_modern: sofaDrawer(SOFA_THEMES.blue!),
  sofa_modern_grey: sofaDrawer(SOFA_THEMES.grey!),
  sofa_modern_red: sofaDrawer(SOFA_THEMES.red!),
  sofa_corner: cornerSofaDrawer(SOFA_THEMES.blue!),
  sofa_corner_grey: cornerSofaDrawer(SOFA_THEMES.grey!),
  sofa_corner_red: cornerSofaDrawer(SOFA_THEMES.red!),
  floor_cushion: drawFloorCushion,
  wooden_bench: drawWoodenBench,
  dining_chair: drawDiningChair,
  dining_table: drawDiningTable,
  side_table: drawSideTable,
  tv_stand: drawTvStand,
  piano: drawPiano,
  fireplace: drawFireplace,
  grandfather_clock: drawGrandfatherClock,
  aquarium: drawAquarium,
  easel: drawEasel,
  floor_vase: drawFloorVase,
  standing_mirror: drawStandingMirror,
  round_rug: rugDrawer('#2f6fa0', '#efe8d4', '#5bb0d8', true),
  plain_rug: rugDrawer('#7a9a5a', '#e8dcb8', '#b5573f', false),

  bed_single: bedDrawer(BED_LINENS.blue!),
  bed_single_white: bedDrawer(BED_LINENS.white!),
  bed_single_grey: bedDrawer(BED_LINENS.grey!),
  bed_frameless: framelessBedDrawer(BED_LINENS.blue!),
  bed_frameless_white: framelessBedDrawer(BED_LINENS.white!),
  bunk_bed: drawBunkBed,
  wardrobe: cabinetDrawer('#a67849', '#c08e5a', 62, 2),
  dresser: drawerChest('#c99562', '#e0b07a', 30, 3),
  nightstand: drawNightstand,
  teddy_bear: drawTeddy,

  fridge: drawFridge,
  stove: drawStove,
  kitchen_counter: drawKitchenCounter,
  kitchen_sink: drawKitchenSink,
  pantry: drawPantry,
  water_dispenser: drawWaterDispenser,

  bathtub: drawBathtub,
  toilet: drawToilet,
  washbasin: drawWashbasin,
  shower: drawShower,
  washing_machine: drawWashingMachine,
  laundry_rack: drawLaundryRack,

  arc_lamp: drawArcLamp,
  table_lamp: drawTableLamp,
  candles: drawCandles,
  paper_lantern: drawPaperLantern,

  wall_low: wallDrawer(22, '#e8dcc0'),
  wall_high: wallDrawer(72, '#e8dcc0'),
  wall_window: wallDrawer(72, '#e8dcc0', { window: true }),
  doorway: wallDrawer(72, '#e8dcc0', { opening: true }),
  door_wood: doorDrawer(66, WOOD),
  door_glass: doorDrawer(66, '#c7ccd1', { glass: true }),
  door_metal: doorDrawer(66, STEEL),
  pillar: drawPillar,
  glass_wall: drawGlassWall,
  wall_corner: cornerWallDrawer(72, '#e8dcc0'),
  glass_corner: drawGlassCorner,
  wall_high_white: wallDrawer(72, WHITE),
  wall_high_pastel: wallDrawer(72, '#d7e6f5'),
  half_wall: wallDrawer(36, '#e8dcc0'),
  wall_shelf: wallShelfDrawer(72, '#e8dcc0'),
  curtain_doorway: curtainDoorwayDrawer(66, '#e8dcc0'),
  window_round: windowRoundDrawer(72, '#e8dcc0'),
  wall_arch_window: wallArchWindowDrawer(72, '#e8dcc0'),
  pillar_square: drawPillarSquare,
  room_divider_screen: roomDividerDrawer(48, WOOD),
  balcony_railing: railingDrawer(WOOD_DARK, WOOD),

  garden_lawn: drawLawn,
  garden_path: drawGardenPath,
  garden_deck: drawGardenDeck,
  fence_wood: fenceDrawer('#b98a5c', '#d3a574'),
  fence_white: fenceDrawer('#f1eee6', '#ffffff'),
  fence_wood_corner: cornerFenceDrawer('#b98a5c', '#d3a574'),
  fence_white_corner: cornerFenceDrawer('#f1eee6', '#ffffff'),
  hedge: drawHedge,
  flower_bed: drawFlowerBed,
  frangipani: drawFrangipani,
  palm_tree: drawPalmTree,
  garden_bench: drawGardenBench,
  pond: drawPond,
  garden_lamp: drawGardenLamp,
  bbq_pit: drawBbqPit,
  hammock: drawHammock,
  umbrella_table: drawUmbrellaTable,
  garden_gnome: drawGnome,
  bird_bath: drawBirdBath,
  wishing_well: drawWishingWell,
  scarecrow: drawScarecrow,

  garden_plot: drawGardenPlot,
  raised_bed: drawRaisedBed,
  greenhouse_bed: drawGreenhouseBed,
  chicken_coop: drawChickenCoop,
  beehive: drawBeehive,
  tool_shed: drawToolShed,
  wheelbarrow: drawWheelbarrow,
  hay_bale: drawHayBale,
  water_barrel: drawWaterBarrel,
};
